import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import validate from "validate.js";
import { isEmpty } from "lodash";
import { DesignationDropdown } from "CommonDropdowns";
import { FormFieldLabel, CustomDropdownContainer } from "CommonComponent";
import { APPRAISAL_TYPE_NAME_OPTIONS, designCodeOptionForName, DESIGN_CODE_OPTIONS } from "./constants";

const schema = {
  name: {
    presence: { allowEmpty: false, message: "^Appraisal type is required" },
  },
  designations: {
    presence: { allowEmpty: false, message: "^Select at least one designation" },
  },
};

const emptyValues = {
  name: "",
  nameValue: null,
  designCode: "",
  designCodeValue: null,
  description: "",
  is_active: "y",
  designations: [],
  designationValue: [],
};

const AddUpdateModal = ({
  show,
  onHide,
  mode = "add",
  record = null,
  onSubmit,
  serverErrors = {},
}) => {
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...emptyValues },
    touched: {},
    errors: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;

    if ((isEdit || isView) && record) {
      const designationValue = (record.designations || []).map((d) => ({
        value: d.id,
        label: d.name,
      }));
      const nameValue =
        APPRAISAL_TYPE_NAME_OPTIONS.find((o) => o.value === record.name) ||
        (record.name ? { value: record.name, label: record.name } : null);
      const designCodeValue =
        designCodeOptionForName(record.name) ||
        (record.code
          ? { value: record.code, label: record.code }
          : null);
      setFormState({
        isValid: true,
        values: {
          name: record.name || "",
          nameValue,
          designCode: designCodeValue?.value || record.code || "",
          designCodeValue,
          description: record.description || "",
          is_active: record.is_active || "y",
          designations: designationValue.map((d) => d.value),
          designationValue,
        },
        touched: {
          name: true,
          designations: true,
          is_active: true,
        },
        errors: {},
      });
      return;
    }

    setFormState({
      isValid: false,
      values: { ...emptyValues },
      touched: {},
      errors: {},
    });
  }, [show, isEdit, isView, record]);

  useEffect(() => {
    const errors = validate(
      {
        name: formState.values.name,
        designations: formState.values.designations,
      },
      schema
    );
    setFormState((prev) => ({
      ...prev,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values.name, formState.values.designations]);

  useEffect(() => {
    if (!isEmpty(serverErrors)) {
      setFormState((prev) => ({
        ...prev,
        isValid: false,
        errors: { ...prev.errors, ...serverErrors },
      }));
    }
  }, [serverErrors]);

  const hasError = (field) =>
    !!(formState.touched[field] && formState.errors[field]);

  const statusOptions = useMemo(
    () => [
      { value: "y", label: "Active" },
      { value: "n", label: "Inactive" },
    ],
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, [name]: value },
      touched: { ...prev.touched, [name]: true },
    }));
  };

  const handleNameChange = (selected) => {
    const designCodeValue = selected
      ? designCodeOptionForName(selected.value)
      : null;
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        name: selected?.value || "",
        nameValue: selected || null,
        designCode: designCodeValue?.value || "",
        designCodeValue,
      },
      touched: { ...prev.touched, name: true },
    }));
  };

  const handleDesignationChange = (selected) => {
    const list = Array.isArray(selected)
      ? selected.filter((o) => o?.value !== "all")
      : [];
    setFormState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        designationValue: list,
        designations: list.map((o) => o.value),
      },
      touched: { ...prev.touched, designations: true },
    }));
  };

  const handleStatusChange = (selected) => {
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, is_active: selected?.value || "y" },
      touched: { ...prev.touched, is_active: true },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormState((prev) => ({
      ...prev,
      touched: {
        name: true,
        designations: true,
        is_active: true,
      },
    }));

    if (!formState.isValid || isView) return;

    setSaving(true);
    try {
      await onSubmit({
        name: formState.values.name.trim(),
        description: formState.values.description || null,
        is_active: formState.values.is_active || "y",
        designations: formState.values.designations,
      });
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === "view"
      ? "View Appraisal Type"
      : mode === "edit"
        ? "Update Appraisal Type"
        : "Add Appraisal Type";

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="font-16">{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <FormFieldLabel required>Appraisal Type</FormFieldLabel>
                <Select
                  name="name"
                  placeholder="Select appraisal type"
                  className={`multi-select w-100${hasError("name") ? " is-invalid" : ""}`}
                  classNamePrefix="react-select"
                  options={APPRAISAL_TYPE_NAME_OPTIONS}
                  value={formState.values.nameValue}
                  onChange={handleNameChange}
                  isDisabled={isView}
                  isSearchable
                  isClearable
                  components={{ ValueContainer: CustomDropdownContainer }}
                />
                {hasError("name") && (
                  <div className="text-danger mt-1 font-13">
                    {formState.errors.name[0]}
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <FormFieldLabel>Status</FormFieldLabel>
                <Select
                  name="is_active"
                  className="multi-select w-100"
                  classNamePrefix="react-select"
                  options={statusOptions}
                  value={statusOptions.find(
                    (o) => o.value === formState.values.is_active
                  )}
                  onChange={handleStatusChange}
                  isDisabled={isView}
                  isSearchable={false}
                  components={{ ValueContainer: CustomDropdownContainer }}
                />
              </div>
            </Col>
            <Col md={6}>
              <DesignationDropdown
                isMulti
                showSelectAllOption={false}
                required
                isDisabled={isView}
                label="Designations"
                placeholder="Select designations"
                designationValue={formState.values.designationValue}
                setDesignationValue={(val) =>
                  handleDesignationChange(Array.isArray(val) ? val : [])
                }
                designationOnChange={handleDesignationChange}
              />
              {hasError("designations") && (
                <div className="text-danger mt-1 font-13 mb-3">
                  {formState.errors.designations[0]}
                </div>
              )}
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <FormFieldLabel>Design Code</FormFieldLabel>
                <Select
                  name="design_code"
                  placeholder="Select appraisal type first"
                  className="multi-select w-100"
                  classNamePrefix="react-select"
                  options={DESIGN_CODE_OPTIONS}
                  value={formState.values.designCodeValue}
                  isDisabled
                  isSearchable={false}
                  isClearable={false}
                  components={{ ValueContainer: CustomDropdownContainer }}
                />
              </div>
            </Col>
            <Col md={12}>
              <div className="mb-1">
                <FormFieldLabel>Description</FormFieldLabel>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  placeholder="Optional description"
                  value={formState.values.description || ""}
                  onChange={handleChange}
                  disabled={isView}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            {isView ? "Close" : "Cancel"}
          </Button>
          {!isView && (
            <Button
              type="submit"
              className="add-new-btn border-0"
              disabled={!formState.isValid || saving}
            >
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddUpdateModal;
